import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Mail, Send, Plus, X, RefreshCw, ShieldCheck, Eye, Clock, ClipboardPaste,
} from 'lucide-react';

/**
 * Soul Welcome Letter · CEO-only one-click invite tile.
 *
 * Adds 1+ new members, mints a Soul Form token for each, sends
 * the branded Concierge® Welcome Letter from `request@concierges.in`
 * with the Soul Profile link embedded.
 *
 * Modes:
 *   • Send now  — fires Resend immediately
 *   • Schedule  — drops into `scheduled_welcome_jobs` for 09:00 IST next day
 *   • Dry-run   — preview only, no Resend call
 *
 * Backend:
 *   POST /api/soul-form-admin/{client_id}/send-welcome
 *   POST /api/soul-form-admin/{client_id}/schedule-welcome
 *   GET  /api/soul-form-admin/{client_id}/welcome-log
 *   GET  /api/soul-form-admin/{client_id}/scheduled-welcome
 */
export default function SoulWelcomeInviteTile({ fixedClientId = null } = {}) {
  const [clientId, setClientId] = useState(fixedClientId || 'jlr');
  const [rows, setRows] = useState([
    { salutation: 'Mr', first_name: '', last_name: '', email: '', phone: '' },
  ]);
  const [sending, setSending] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(true);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [scheduled, setScheduled] = useState([]);

  const fetchLog = async (cid = clientId) => {
    setLogLoading(true);
    try {
      const r = await api.get(
        `/soul-form-admin/${cid}/welcome-log`, { params: { limit: 8 } });
      setLog(r.data.rows || []);
    } catch (e) {
      // Silent — log is decorative
    } finally {
      setLogLoading(false);
    }
    try {
      const r = await api.get(`/soul-form-admin/${cid}/scheduled-welcome`);
      setScheduled(r.data.jobs || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchLog(); /* eslint-disable-next-line */ }, [clientId]);

  const parsePasted = (raw) => {
    // Accept comma OR tab OR pipe-separated. Detect header row by
    // looking for `email` token. Each parsed row → push into `rows`.
    const lines = raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    let header = null;
    let dataLines = lines;
    if (/\bemail\b/i.test(lines[0])) {
      header = lines[0].split(/[,\t|]/).map(s => s.trim().toLowerCase());
      dataLines = lines.slice(1);
    } else {
      // Assume positional: salutation, first_name, last_name, email, phone
      header = ['salutation', 'first_name', 'last_name', 'email', 'phone'];
    }
    const parsed = [];
    for (const line of dataLines) {
      const parts = line.split(/[,\t|]/).map(s => s.trim());
      const row = { salutation: 'Mr', first_name: '', last_name: '', email: '', phone: '' };
      header.forEach((col, i) => {
        const v = parts[i] || '';
        if (col === 'salutation' || col === 'first_name'
            || col === 'last_name' || col === 'email' || col === 'phone') {
          row[col] = v;
        } else if (col === 'first name') row.first_name = v;
        else if (col === 'last name') row.last_name = v;
      });
      if (row.email && row.email.includes('@')) parsed.push(row);
    }
    return parsed;
  };

  const handlePasteApply = () => {
    const parsed = parsePasted(pasteText);
    if (!parsed.length) {
      toast.error('No valid rows detected. Expected columns: salutation, first_name, last_name, email, phone');
      return;
    }
    if (parsed.length > 25) {
      toast.error(`Pasted ${parsed.length} rows but max 25 per batch — first 25 imported`);
    }
    setRows(parsed.slice(0, 25));
    setPasteOpen(false);
    setPasteText('');
    toast.success(`Imported ${Math.min(parsed.length, 25)} members from paste`);
  };

  const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      return `${dd}/${mm}/${yy} · ${hh}:${mi}`;
    } catch (e) { return iso; }
  };

  const updateRow = (i, k, v) => {
    const next = [...rows];
    next[i] = { ...next[i], [k]: v };
    setRows(next);
  };
  const addRow = () => {
    if (rows.length >= 25) {
      toast.error('Send max 25 per batch — split larger lists');
      return;
    }
    setRows([...rows, { salutation: 'Mr', first_name: '', last_name: '', email: '', phone: '' }]);
  };
  const removeRow = (i) => {
    if (rows.length === 1) {
      setRows([{ salutation: 'Mr', first_name: '', last_name: '', email: '', phone: '' }]);
    } else {
      setRows(rows.filter((_, idx) => idx !== i));
    }
  };

  const send = async ({ schedule = false } = {}) => {
    // Validate
    const valid = rows.filter(r => r.first_name?.trim() && r.email?.trim());
    if (!valid.length) {
      toast.error('Add at least one row with first name + email');
      return;
    }
    const invalid = valid.filter(r => !r.email.includes('@'));
    if (invalid.length) {
      toast.error('One or more emails are invalid');
      return;
    }

    let verb = 'Send';
    if (schedule) verb = 'Schedule (for tomorrow 09:00 IST)';
    else if (dryRun) verb = 'Preview';
    if (!window.confirm(
      `${verb} the Concierge® Welcome Letter to ${valid.length} member${valid.length === 1 ? '' : 's'}?` +
      (dryRun && !schedule ? '\n\n(Dry-run — no emails will go out)' :
        schedule ? '\n\nThe batch is queued for delivery at 09:00 IST tomorrow.' :
        '\n\nSent FROM request@concierges.in. Each member gets their own Soul Profile link.'),
    )) return;

    setSending(true);
    try {
      const url = schedule
        ? `/soul-form-admin/${clientId}/schedule-welcome`
        : `/soul-form-admin/${clientId}/send-welcome`;
      const r = await api.post(url, { recipients: valid, dry_run: dryRun });
      const d = r.data;

      if (schedule) {
        toast.success(`Scheduled — ${d.recipient_count || valid.length} member${valid.length === 1 ? '' : 's'} for ${d.scheduled_for_label || '09:00 IST tomorrow'}`);
        setRows([{ salutation: 'Mr', first_name: '', last_name: '', email: '', phone: '' }]);
      } else if (d.dry_run) {
        toast.success(`Dry-run OK — would send to ${d.total} member${d.total === 1 ? '' : 's'} from ${d.sender}`);
      } else {
        const sent = d.sent || 0;
        const failed = d.failed || 0;
        if (failed === 0) {
          toast.success(`Welcome letter sent to ${sent} member${sent === 1 ? '' : 's'}`);
          setRows([{ salutation: 'Mr', first_name: '', last_name: '', email: '', phone: '' }]);
        } else if (sent === 0) {
          toast.error(`All ${failed} sends failed — check log for Resend errors`);
        } else {
          toast.warning(`${sent} sent · ${failed} failed — review log`);
        }
      }
      fetchLog();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`${verb} failed: ${detail}`);
    } finally {
      setSending(false);
    }
  };

  const cancelScheduled = async (jobId) => {
    if (!window.confirm('Cancel this scheduled welcome batch?')) return;
    try {
      await api.post(`/soul-form-admin/${clientId}/scheduled-welcome/${jobId}/cancel`);
      toast.success('Scheduled batch cancelled');
      fetchLog();
    } catch (e) {
      toast.error(`Cancel failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="rounded-xl border p-4"
      style={{ borderColor: '#E5E1DC',
               background: 'linear-gradient(135deg, #FAF8F5 0%, #F4EDDF 100%)' }}
      data-testid="soul-welcome-invite-tile">
      {/* HEADER */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#1B2A4A' }}>
            <Mail size={18} style={{ color: '#F5C86B' }} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>
                Welcome Letter + Soul Profile
              </p>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold"
                style={{ backgroundColor: '#B88C40', color: '#FFFFFF' }}>
                From request@
              </span>
            </div>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              Membership activation + Soul Form link · Resend · audit-logged
            </p>
          </div>
        </div>
        <button onClick={() => fetchLog()} disabled={logLoading}
          title="Refresh log"
          className="px-2 py-1.5 rounded text-[11px] font-medium border hover:bg-gray-50 inline-flex items-center gap-1 disabled:opacity-60"
          style={{ borderColor: '#9CA3AF', color: '#1B2A4A' }}
          data-testid="soul-welcome-refresh-btn">
          <RefreshCw size={11} className={logLoading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-md border bg-white p-2.5" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-[10px] uppercase tracking-wider font-semibold mb-1"
             style={{ color: '#6B7280' }}>Client</p>
          {fixedClientId ? (
            <div className="text-[12.5px] font-medium" style={{ color: '#1B2A4A' }}
                  data-testid="soul-welcome-client-locked">
              {fixedClientId.toUpperCase()} <span style={{ color: '#9CA3AF' }}>· locked</span>
            </div>
          ) : (
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}
              className="w-full text-[12.5px] font-medium bg-transparent outline-none"
              style={{ color: '#1B2A4A' }}
              data-testid="soul-welcome-client-select">
              <option value="jlr">JLR</option>
              <option value="aabe">Aabe Bharat</option>
              <option value="dreamfolks">DreamFolks</option>
              <option value="xiaomi">Xiaomi</option>
              <option value="bhutan">Bhutan</option>
            </select>
          )}
        </div>
        <label className="rounded-md border bg-white p-2.5 cursor-pointer hover:bg-gray-50"
          style={{ borderColor: '#E5E1DC' }}>
          <p className="text-[10px] uppercase tracking-wider font-semibold mb-1"
             style={{ color: '#6B7280' }}>Mode</p>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              data-testid="soul-welcome-dryrun-checkbox" />
            <span className="text-[12px] font-medium" style={{ color: '#1B2A4A' }}>
              Dry-run (no send)
            </span>
          </div>
        </label>
      </div>

      {/* Recipient rows */}
      <div className="space-y-2 mb-3" data-testid="soul-welcome-rows">
        {rows.map((row, i) => (
          <div key={i} className="rounded-md border bg-white p-2.5"
            style={{ borderColor: '#E5E1DC' }}
            data-testid={`soul-welcome-row-${i}`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider"
                style={{ color: '#6B7280' }}>
                Member {i + 1}
              </span>
              <button onClick={() => removeRow(i)}
                title="Remove row"
                className="text-[11px] hover:text-red-600 inline-flex items-center gap-1"
                style={{ color: '#9CA3AF' }}
                data-testid={`soul-welcome-row-remove-${i}`}>
                <X size={11} />
              </button>
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              <select value={row.salutation}
                onChange={(e) => updateRow(i, 'salutation', e.target.value)}
                className="text-[12px] px-1.5 py-1.5 rounded border outline-none"
                style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                data-testid={`soul-welcome-salutation-${i}`}>
                <option>Mr</option><option>Ms</option><option>Mrs</option>
                <option>Dr</option><option>Capt</option><option>Maj</option>
              </select>
              <input type="text" placeholder="First name"
                value={row.first_name}
                onChange={(e) => updateRow(i, 'first_name', e.target.value)}
                className="text-[12px] px-2 py-1.5 rounded border outline-none"
                style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                data-testid={`soul-welcome-first-${i}`} />
              <input type="text" placeholder="Last name"
                value={row.last_name}
                onChange={(e) => updateRow(i, 'last_name', e.target.value)}
                className="text-[12px] px-2 py-1.5 rounded border outline-none"
                style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                data-testid={`soul-welcome-last-${i}`} />
              <input type="email" placeholder="email@…"
                value={row.email}
                onChange={(e) => updateRow(i, 'email', e.target.value)}
                className="text-[12px] px-2 py-1.5 rounded border outline-none"
                style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                data-testid={`soul-welcome-email-${i}`} />
              <input type="text" placeholder="+91 …"
                value={row.phone}
                onChange={(e) => updateRow(i, 'phone', e.target.value)}
                className="text-[12px] px-2 py-1.5 rounded border outline-none"
                style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                data-testid={`soul-welcome-phone-${i}`} />
            </div>
          </div>
        ))}
      </div>

      {/* Bulk paste option — power-user shortcut for adding many at once */}
      <div className="mb-3" data-testid="soul-welcome-paste-section">
        <button onClick={() => setPasteOpen(!pasteOpen)}
          className="text-[11px] inline-flex items-center gap-1.5 hover:underline"
          style={{ color: '#B88C40' }}
          data-testid="soul-welcome-paste-toggle">
          <ClipboardPaste size={11} />
          {pasteOpen ? 'Hide paste-bulk' : 'Paste a list (CSV / Excel column) ↓'}
        </button>
        {pasteOpen && (
          <div className="mt-2 rounded-md border p-2.5 bg-white"
            style={{ borderColor: '#E5E1DC' }}>
            <p className="text-[10.5px] mb-2" style={{ color: '#6B7280' }}>
              Paste rows from Excel / CSV. First row can be a header, OR
              positional order: <code>salutation, first_name, last_name, email, phone</code>.
              Separated by commas, tabs, or pipes.
            </p>
            <textarea value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              rows={5}
              placeholder={"Mr,Aarav,Patel,aarav@example.com,+91 9876543210\nMs,Priya,Shah,priya@example.com,+91 9999912345"}
              className="w-full text-[11.5px] font-mono p-2 rounded border outline-none"
              style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
              data-testid="soul-welcome-paste-textarea" />
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => { setPasteText(''); setPasteOpen(false); }}
                className="text-[11px] px-3 py-1.5 rounded border hover:bg-gray-50"
                style={{ borderColor: '#9CA3AF', color: '#6B7280' }}>
                Cancel
              </button>
              <button onClick={handlePasteApply} disabled={!pasteText.trim()}
                className="text-[11px] px-3 py-1.5 rounded text-white hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: '#B88C40' }}
                data-testid="soul-welcome-paste-apply">
                Import & replace rows
              </button>
            </div>
          </div>
        )}
      </div>

      <button onClick={addRow}
        className="w-full text-[11.5px] py-2 rounded border-dashed border-2 inline-flex items-center justify-center gap-1.5 mb-3 hover:bg-gray-50"
        style={{ borderColor: '#B88C40', color: '#B88C40' }}
        data-testid="soul-welcome-add-row-btn">
        <Plus size={12} /> Add another member
      </button>

      {/* Send buttons — Send Now + Schedule for tomorrow 09:00 IST */}
      <div className="grid grid-cols-2 gap-2" data-testid="soul-welcome-send-row">
        <button onClick={() => send({ schedule: false })} disabled={sending}
          className="px-3 py-3 rounded-md text-[12.5px] font-semibold text-white inline-flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-60"
          style={{ backgroundColor: '#1B2A4A' }}
          data-testid="soul-welcome-send-btn">
          {sending ? (
            <><RefreshCw size={13} className="animate-spin" />…</>
          ) : (
            <>{dryRun ? <Eye size={13} /> : <Send size={13} />}
              {dryRun ? 'Preview send' : 'Send now'}</>
          )}
        </button>
        <button onClick={() => send({ schedule: true })}
          disabled={sending || dryRun}
          title={dryRun ? 'Disable dry-run to schedule a real send' : 'Queue for 09:00 IST tomorrow'}
          className="px-3 py-3 rounded-md text-[12.5px] font-semibold inline-flex items-center justify-center gap-2 transition-opacity hover:opacity-90 disabled:opacity-50 border"
          style={{ backgroundColor: '#FFFFFF',
                    color: '#1B2A4A', borderColor: '#1B2A4A' }}
          data-testid="soul-welcome-schedule-btn">
          <Clock size={13} />
          Schedule for 09:00 IST
        </button>
      </div>
      <p className="text-[10px] mt-1.5 text-center" style={{ color: '#9CA3AF' }}>
        Scheduled batches go out at 09:00 IST the following morning — when members are more likely to read.
      </p>

      {/* Scheduled queue */}
      {scheduled.length > 0 && (
        <div className="mt-4 pt-3 border-t" style={{ borderColor: '#E5E1DC' }}
          data-testid="soul-welcome-scheduled">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={11} style={{ color: '#B88C40' }} />
            <span className="text-[10px] font-semibold uppercase tracking-wider"
                  style={{ color: '#1B2A4A' }}>
              Scheduled batches ({scheduled.length})
            </span>
          </div>
          <ul className="space-y-1.5">
            {scheduled.map((s, i) => (
              <li key={s.job_id || i}
                className="text-[11px] flex items-start justify-between gap-2 rounded p-2"
                style={{ background: '#FEF8E7', color: '#7A4F08' }}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold" style={{ color: '#1B2A4A' }}>
                    {s.recipient_count} member{s.recipient_count === 1 ? '' : 's'} · {s.scheduled_for_label}
                  </div>
                  <div className="text-[10px]" style={{ color: '#6B7280' }}>
                    scheduled by {s.scheduled_by} · {fmtDateTime(s.scheduled_at)}
                  </div>
                </div>
                <button onClick={() => cancelScheduled(s.job_id)}
                  className="text-[10.5px] hover:underline"
                  style={{ color: '#B91C1C' }}
                  data-testid={`soul-welcome-cancel-${i}`}>
                  cancel
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Activity log */}
      <div className="mt-4 pt-3 border-t" style={{ borderColor: '#E5E1DC' }}
        data-testid="soul-welcome-log">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={11} style={{ color: '#2A7B88' }} />
          <span className="text-[10px] font-semibold uppercase tracking-wider"
                style={{ color: '#1B2A4A' }}>
            Recent sends ({log.length})
          </span>
        </div>
        {log.length === 0 ? (
          <p className="text-[11px] italic" style={{ color: '#6B7280' }}>
            No welcome letters sent yet for {clientId.toUpperCase()}.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {log.map((r, i) => (
              <li key={i} className="text-[11px] flex items-start gap-2 rounded p-1.5"
                style={{ background: r.ok ? '#F0FDF4' : '#FEF2F2',
                          color: r.ok ? '#15803D' : '#B91C1C' }}>
                <span className="font-mono" style={{ color: r.ok ? '#16A34A' : '#DC2626' }}>
                  {r.ok ? '✓' : '✗'}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="truncate" style={{ color: '#1B2A4A' }}>
                    <strong>{r.member_name || r.to}</strong>
                    <span style={{ color: '#6B7280' }}> &middot; {r.to}</span>
                  </div>
                  <div className="text-[10px]" style={{ color: '#6B7280' }}>
                    {fmtDateTime(r.sent_at)} &middot; by {r.sent_by}
                    {r.resend_id && <span className="font-mono"> &middot; {r.resend_id.slice(0, 14)}…</span>}
                    {r.error && <span style={{ color: '#B91C1C' }}> &middot; {r.error}</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Doctrine */}
      <div className="mt-3 pt-3 border-t text-[10.5px] leading-relaxed"
        style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>
        <strong style={{ color: '#1B2A4A' }}>What goes out:</strong>{' '}
        a single email containing the membership login block (mobile = username,
        OTP password) and the member's personal Soul Profile link.
        Sent from <code style={{ color: '#B88C40' }}>request@concierges.in</code>
        {' '}so replies land at the team's shared inbox. Each Soul URL is unique +
        token-gated + expires after 30 days.
      </div>
    </div>
  );
}
